import { genUploader } from "uploadthing/client"

import {
  buildCapturedAtFromFile,
  serializeUploadReference,
  type PublicUploadReference,
} from "@/features/public/report/upload-reference"

export interface UploadIncidentEvidenceInput {
  onStatusChange?: (message: string) => void
  photos: File[]
  voiceNote: File | null
}

interface UploadthingResult {
  fileHash?: string | null
  key?: string | null
  name?: string | null
  size?: number | null
  type?: string | null
  ufsUrl?: string | null
  url?: string | null
}

interface UploadIncidentEvidenceResult {
  uploadedPhotos: PublicUploadReference[]
  uploadedVoiceNotes: PublicUploadReference[]
}

let uploaderPromise: Promise<ReturnType<typeof genUploader>> | null = null

async function getUploader() {
  if (!uploaderPromise) {
    uploaderPromise = Promise.resolve(
      genUploader({
        url: `${window.location.origin}/api/uploadthing`,
      })
    )
  }

  return uploaderPromise
}

export async function uploadIncidentEvidence({
  photos,
  voiceNote,
  onStatusChange,
}: UploadIncidentEvidenceInput): Promise<UploadIncidentEvidenceResult> {
  if (photos.length === 0 && !voiceNote) {
    return {
      uploadedPhotos: [],
      uploadedVoiceNotes: [],
    }
  }

  const uploader = await getUploader()
  const uploadedPhotos: PublicUploadReference[] = []
  const uploadedVoiceNotes: PublicUploadReference[] = []

  if (photos.length > 0) {
    const photoResults = (await uploader.uploadFiles("incidentPhoto", {
      files: photos,
      onUploadProgress: ({ totalProgress }) => {
        onStatusChange?.(`Uploading evidence (${Math.max(totalProgress, 1)}%)...`)
      },
    })) as UploadthingResult[]

    uploadedPhotos.push(
      ...photoResults.map((result, index) =>
        serializeUploadReference(result, {
          capturedAt: buildCapturedAtFromFile(photos[index]),
          uploadMetadata: {
            source: "public-report",
            slot: `photo-${index + 1}`,
          },
        })
      )
    )
  }

  if (voiceNote) {
    const [voiceResult] = (await uploader.uploadFiles("incidentVoiceNote", {
      files: [voiceNote],
      onUploadProgress: ({ totalProgress }) => {
        onStatusChange?.(
          `Uploading voice note (${Math.max(totalProgress, 1)}%)...`
        )
      },
    })) as UploadthingResult[]

    if (voiceResult) {
      uploadedVoiceNotes.push(
        serializeUploadReference(voiceResult, {
          capturedAt: buildCapturedAtFromFile(voiceNote),
          uploadMetadata: {
            source: "public-report",
            slot: "voice-note-1",
          },
        })
      )
    }
  }

  onStatusChange?.("Upload complete. Finalizing report...")

  return {
    uploadedPhotos,
    uploadedVoiceNotes,
  }
}
